import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from "react-router-dom";
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import mergeWith from 'lodash.mergewith';
import merge from 'lodash.merge';
import cloneDeep from 'lodash.clonedeep';
import AlbumSelector from "../album-selector/";
import ArtistSelector from "../artist-selector/";
import AddAlbumHeader from './add-album-header';
import AddReview from "../add-review/";
import DatePicker from '../date-picker/';
import ImageUploader from '../image-uploader/image-uploader';
import LabelSelector from '../label-selector';
import GenreSelector from '../genre-selector';
import InputField from '../input-field';
import ReviewSelector from '../review-selector/';
import LoadingAnimation from '../loading-animation';
import { UserContext } from "../../providers/user-provider";
import Modal from '../modal/modal';
import { generateId } from '../../utilities/';
import * as Actions from '../../redux/actions/';
import { addAlbum, getAlbum } from "../../api/";
import { dateToTimestamp } from '../../firebase';
import { fromString } from 'html-to-text';
import './add-album.scss';
const defaultAlbumState = {
	artist: {},
	description: '',
	genres: [],
	id: null,
	image: null,
	isLegacy: false,
	labels: [],
	name: '',
	releaseDate: null,
	reviewDate: null,
	reviews: {},
	reviewers: [],
	score: 0,
	status: ['draft'],
}
// TODO: Split this up into smaller components. Refactor useState to useReducer
const AddAlbum = ({ allGenres, allLabels, addNotification, albumData, albums, getCollectionSubscription, impersonationUser, match, mode, onSubmit, onExit, saveAlbum }) => {
	let query = useLocation().search;
	let queryEdit = useRef(query ? query.split("?edit-review=")[1] : null);
	let reviewDateOverride = useRef(false);
	const [albumEditId, setAlbumEditId] = useState(match && match.params && match.params.slug);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [editTab, setEditTab] = useState('album');
	const [lastSaveTime, setLastSaveTime] = useState(null);
	const [reviewEditId, setReviewEditId] = useState(null);
	const userContext = useContext(UserContext);
	const user = impersonationUser || userContext;
	const [albumState, setAlbumState] = useState(defaultAlbumState);

	function updateAlbumState(newState) {
		setAlbumState(prevState => {
			const newVal = {
	        ...prevState,
	        ...newState
	    };
			return newVal;
		});
	};

	useEffect(() => {
		let unsubFromLabels;
		let unsubFromGenres;
		unsubFromLabels = getCollectionSubscription('labels');
		unsubFromGenres = getCollectionSubscription('genres');

		async function getAlbumData() {
			let thisAlbum;

			if (albumEditId) {
				// if the album is already in the store use that, otherwise fetch it'
				thisAlbum = albums.find(album => album.id === albumEditId);

				setIsLoading(true);
				if (!thisAlbum) {
					try {
						thisAlbum = await getAlbum(albumEditId);
					} catch {
						// TODO: Need to be able to set the album selector in a selected state here.
						setIsLoading(false);
						return;
					}
				}

				setAlbumState(thisAlbum);
			} else if (albumData) {
				setAlbumState(albumData);
			} else {
				setAlbumState(defaultAlbumState); // Why is this necessary?
			}
			setIsLoading(false);
		}
		getAlbumData();
		return () => {
			unsubFromGenres && unsubFromGenres();
			unsubFromLabels && unsubFromLabels();
		}
	}, [albumData, albumEditId, albums, getCollectionSubscription]);

	useEffect(() => {
		if (queryEdit.current && !isLoading) {
			queryEdit.current = null;
			setEditTab('reviews');
		}
	}, [albumState, isLoading, queryEdit, userContext.role]);


	const handleExitClick = () => {
		onExit && onExit();
	};

	function handleReviewChange (data, reviewId) {
		const albumReviews = merge({}, albumState.reviews, {[reviewId]: data});

		reviewDateOverride.current = true;
		updateAlbumState({reviews: albumReviews});
	}

	function handleArtistSelected (artist) {
		updateAlbumState({artist: artist});
	}

	function handleArtistDeselected () {
		updateAlbumState({name: ''});
	}

	const handleUploadSuccess = (filename) => {
		updateAlbumState({image: filename});
	}

	const handleUploadError = (error) => {
		console.log('error', error);
	}

	const handleDateChange = (date) => {
		date && updateAlbumState({releaseDate: date});
	}

	const handleGenreChange = (data) => {
		updateAlbumState({genres: data});
	}

	const handleLabelChange = (data) => {
		updateAlbumState({labels: data});
	}

	function handleAlbumSelect (album) {
		setAlbumEditId(album && album.id);
	}

	function handleAlbumDeselect (album) {
		updateAlbumState({name: album});
	}

	function handleBlurWithNewAlbum (album) {
		const newAlbum = {id: generateId(album, albumState.artist.id), name: album};
		handleAlbumSelect(newAlbum);
		updateAlbumState(newAlbum);
	}

	function handleAlbumDetailsTabClick () {
		setEditTab('album');
	}

	function handleReviewsTabClick () {
		setEditTab('reviews');
	}

	function isArtistSelected () {
		return albumState.artist && albumState.artist.id
	}

	function albumDataLocked () {
		return albumState.status.indexOf('approved') >= 0 && userContext.role !== 'owner';
	}

	function calculateAlbumData (albumData, review, index) {
		if (review.status === 'approved') {
			const approvedDate = albumData.reviewDate;
			const reviewAuthor = review.author;
			const reviewDate = review.reviewDate;
			let earliestDate = (approvedDate && approvedDate.valueOf() < reviewDate.valueOf()) ? approvedDate : reviewDate;

			albumData.score = albumData.score ? (albumData.score + index * review.score) / (index + 1) : review.score;

			if (albumState.isLegacy) {
				const albumLegacyDate = albumData.legacyDate;
				const reviewLegacyDate = review.legacyDate;
				albumData.legacyDate = (albumLegacyDate && albumLegacyDate.valueOf() < reviewLegacyDate.valueOf()) ? albumLegacyDate : reviewLegacyDate;
				albumData.reviewDate = dateToTimestamp(new Date());
			} else {
				albumData.reviewDate = earliestDate;
			}

			if (albumData.reviewers.indexOf(reviewAuthor) < 0) {
				albumData.reviewers.push(reviewAuthor);
			}
		}
		if (albumData.status.indexOf(review.status) < 0) {
			albumData.status.push(review.status);
		}

		return albumData;
	}

	function buildAlbumDataFromReviews (reviewData) {
		const reviews = Object.values(reviewData);

		return reviews.reduce(calculateAlbumData, {reviews: reviewData, reviewers: [], score: 0, reviewDate: null, status: []});
	}

	async function handleSave(reviewStatus) {
		const reviewId = reviewEditId || user.id;
		const selectedReview = albumState.reviews[reviewId] ? merge({}, albumState.reviews[reviewId], {status: reviewStatus}) : null;
		let newData;

		if (selectedReview) {
			const plainText = `${fromString(selectedReview.content, {ignoreHref: true, ignoreImage: true, wordwrap: null}).slice(0, 195).trim()}...`;
			selectedReview.id = reviewId;
			selectedReview.author = selectedReview.author || user.name;
			selectedReview.authorId = selectedReview.authorId || user.id;
			selectedReview.authorPhoto = selectedReview.authorPhoto === null ? null : (selectedReview.authorPhoto || user.photoURL);
			selectedReview.previewText = plainText;

			if (!(reviewDateOverride.current === true && selectedReview.reviewDate)) {
				selectedReview.reviewDate = dateToTimestamp(new Date());
			}

			const albumReviews = merge({}, albumState.reviews, {[selectedReview.id]: selectedReview});
			const reviewDerivedData = buildAlbumDataFromReviews(albumReviews);

			newData = mergeWith({}, albumState, reviewDerivedData, (objVal, srcVal) => {
				if (Array.isArray(objVal)) {
					return srcVal;
				}
				return;
			});
		} else {
			newData = cloneDeep(albumState);
		}

		newData.id = newData.id || generateId(newData.name, newData.artist.id);

		setIsSaving(true);

		if (reviewEditId) {
			setReviewEditId(null);
		}

		addAlbum(newData).then(() => {
			let message;
			if (reviewStatus === 'pending') {
				message = `Your review of ${albumState.name} has been submitted for approval.`;
			} else {
				message = `Successfully saved your review of ${albumState.name} `;
			}
			saveAlbum(newData);
			setIsSaving(false);
			setLastSaveTime(moment().format('LT'));
			updateAlbumState(newData);
			addNotification({message: message});
		});
	};

	const renderForm = () => {
		return (
			<div className="admin-app-page">
				{isSaving && !isLoading && <LoadingAnimation text="Saving..." showMask />}
				{isLoading && <LoadingAnimation text="One moment, please..." showMask /> }
					<>
						<AddAlbumHeader
							activeTab={editTab}
							albumData={albumState}
							lastSaveTime={lastSaveTime}
							onAlbumTabClick={handleAlbumDetailsTabClick}
							onReviewsTabClick={handleReviewsTabClick}
							onSave={handleSave}
						/>
						{editTab === 'album' &&
							<>
							{albumState.status.indexOf('approved') >= 0 && userContext.role !== 'owner' &&
								<div className="page-notification">Approved album details cannot be edited. You can still add or modify your review on the Review Content tab. Sorry if I'm out of style.</div>
							}
							<div className="album-form col-layout layout-33">
								<div className="album-fields-1">
									<ImageUploader
										onUploadSuccess={handleUploadSuccess}
										onUploadError={handleUploadError}
										value={albumState.image}
										disabled={albumDataLocked()}
									 />
									{ albumEditId || (albumData && albumData.artist) ?
										<InputField
											disabled
											id="artist"
											label="Artist"
											value={albumState.artist.name || ''}
											onChange={(evt) => {
												const artistState = albumState.artist;
												const newState = {name: evt.target.value}
												updateAlbumState({ artist: {...artistState, ...newState} });
											}}
										/> :
										<ArtistSelector
											artistData={ albumState.artist }
											disabled={albumDataLocked()}
											onDeselect={ handleArtistDeselected }
											onSelect={ handleArtistSelected }
											allowAdd
										/>
									}
									<AlbumSelector
										albumData={ albumState }
										artistData={ albumState.artist }
										id="name"
										disabled={albumDataLocked() || !isArtistSelected()}
										hideList
										label="Album Name"
										onBlurWithNewAlbum={handleBlurWithNewAlbum}
										onChange={(val) => { updateAlbumState({name: val})}}
										onSelect={handleAlbumSelect}
										onDeselect={handleAlbumDeselect}
									/>
								</div>
								<div className="album-fields-2">
									<LabelSelector
										disabled={albumDataLocked() || !isArtistSelected()}
										label="Album Label(s)"
										labels={allLabels}
										selected={albumState.labels}
										zIndex={2}
										onChange={handleLabelChange}
									/>
									<DatePicker
										disabled={albumDataLocked() || !isArtistSelected()}
										id="release-date-picker"
										label="Release Date"
										onChange={handleDateChange}
										value={albumState.releaseDate}
									/>
									<GenreSelector
										disabled={albumDataLocked() || !isArtistSelected()}
										label="Genre(s)"
										genres={allGenres}
										selected={albumState.genres}
										onChange={handleGenreChange}
									/>
									<InputField
										disabled={albumDataLocked() || !isArtistSelected()}
										id="description"
										label="Description"
										maxLength="140"
										onChange={(evt) => {
											updateAlbumState({description: evt.target.value});
										}}
										placeholder="This will display in home page callouts"
										type="textarea"
										value={albumState.description}
									/>
								</div>
								<div className="album-fields-3">
									{albumState.reviewDate &&
										<DatePicker
											disabled
											id="reviewDate"
											label="First Review Date"
											value={albumState.reviewDate}
										/>
									}
									{albumState.score > 0 &&
										<InputField
											disabled
											id="averageScore"
											label="Average Score"
											type="number"
											value={(albumState.score && albumState.score/10) || 0}
										/>
									}
									{userContext.role === 'owner' &&
										<InputField
											id="isLegacy"
											label="Legacy Review"
											onChange={(evt) => {
												updateAlbumState({isLegacy: evt.target.checked});
											}}
											checked={albumState.isLegacy ? ['Yes'] : []}
											disabled={[]}
											type="checkbox"
											value={['Yes']}
										/>
									}
									{userContext.role === 'owner' && Object.values(albumState.reviews).length > 0 &&
										<ReviewSelector
											onReviewClick={(id) => {
												if (id === user.id) {
													setEditTab('reviews');
												} else {
													setReviewEditId(id);
												}
											}}
											reviews={Object.values(albumState.reviews)}
											showAuthor
										/>
									}
								</div>
						</div>
					</>
					} { editTab === 'reviews' &&
						<AddReview
							albumData={{artist: albumState.artist.name, album: albumState.name, isLegacy: albumState.isLegacy}}
							reviewData={albumState.reviews[user.id] || {}}
							onChange={handleReviewChange}
						/>
					}
					{ reviewEditId !== null &&
						<Modal
							title="Edit Review"
							onExitClick={() => {setReviewEditId(null);}}
							primarySubmitLabel="Approve Review"
							secondarySubmitLabel="Save as Pending"
							onPrimarySubmit={() => { handleSave('approved'); }}
							onSecondarySubmit={() => { handleSave('pending'); }}
							key="edit_review_modal"
						>
							<AddReview
								albumData={{artist: albumState.artist.name, album: albumState.name, isLegacy: albumState.isLegacy}}
								reviewData={albumState.reviews[reviewEditId]}
								onChange={handleReviewChange}
							/>
						</Modal>
					}
				</>
			</div>
		);
	};

	const renderPage = () => {
		if (mode === 'modal') {
			return (
				<Modal
					title="Add an Album"
					onExitClick={handleExitClick}
					onPrimarySubmit={handleSave}
					key="add_album_modal"
				>
					<div className="album-editor">
						{ renderForm() }
					</div>
				</Modal>
			);
		}
		return renderForm();
	};

	return renderPage();
};

AddAlbum.propTypes = {
	impersonationUser: PropTypes.object,
	onSubmit: PropTypes.func
}

const mapStateToProps = state => {
	return {
		albums: state.albums,
		allGenres: state.genres,
		allLabels: state.labels,
		impersonationUser: state.impersonationUser
  }
}

const mapDispatchToProps = {
	addNotification: Actions.addNotification,
	getCollectionSubscription: Actions.getCollectionSubscription,
	saveAlbum: Actions.saveAlbum
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(AddAlbum)
